import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addBetToBetSlip, removeBetFromBetSlip } from 'stores/betSlip';
import { selectEventById } from 'stores/outrights';
import usePrevious from 'toolkit/useHooks/usePrevious';
import useOddTransformer from 'toolkit/useHooks/useOddsTransformer.js';
import { AWS_BUCKET_URL } from 'toolkit/constants';

// Build the synthetic "match" shape consumed by the betSlip reducer + downstream
// transformers. checkIfLiveEvent(match) reads match.markets[].producer; outrights
// always use producer=1 so it returns false. isSeason=true tells oddsTransformer
// this is an outright.
function buildOutrightMatch(event) {
  if (!event?.se) return null;
  const se = event.se;
  return {
    providerId: se.id,
    gameDate: se.schedule_StartTime,
    schedule_StartTime: se.schedule_StartTime,
    schedule_EndTime: se.schedule_EndTime,
    isSeason: true,
    isMatch: false,
    isOutright: true,
    sport: se.sportId,
    sportType: se.sportId,
    eventName: se.eventName,
    homeName: se.eventName,
    awayName: '',
    league: se.seasonInfo?.tournamentName || '',
    category: se.seasonInfo?.categoryName || '',
    seasonInfo: se.seasonInfo || null,
    markets: event.markets || []
  };
}

function OutrightOddButton({ event, market, outcome, betBox }) {
  const dispatch = useDispatch();
  const bets = useSelector((state) => state.betSlip.bets);
  const oddsTransformer = useOddTransformer();

  const liveEvent = useSelector((state) =>
    event?.se?.id ? selectEventById(state, event.se.id) : null
  );
  const sourceEvent = liveEvent || event;
  const liveMarket = useMemo(() => {
    if (!sourceEvent?.markets || !market?.stringId) return market;
    return sourceEvent.markets.find((m) => m.stringId === market.stringId) || market;
  }, [sourceEvent, market]);
  const liveOutcome = useMemo(() => {
    if (!liveMarket?.outcomes || !outcome?.id) return outcome;
    return liveMarket.outcomes.find((o) => o.id === outcome.id) || outcome;
  }, [liveMarket, outcome]);

  const providerId = sourceEvent?.se?.id;
  const marketStringId = liveMarket?.stringId;
  const outcomeId = liveOutcome?.id;
  const odds = typeof liveOutcome?.odds === 'number' ? liveOutcome.odds : null;
  const disabled = liveOutcome?.enabled === false || odds == null || odds <= 1;

  const isSelected = useMemo(
    () =>
      bets.some(
        (b) =>
          b.providerId === providerId &&
          b.marketStringId === marketStringId &&
          b.outcomeId === outcomeId &&
          !b.isBetBuilder
      ),
    [bets, providerId, marketStringId, outcomeId]
  );

  const prevOdds = usePrevious(odds);
  const [oddClass, setOddClass] = useState('');
  const flashTimer = useRef(null);
  useEffect(() => {
    if (prevOdds && odds && odds !== prevOdds) {
      setOddClass(` odd-${odds > prevOdds ? 'increased' : 'decreased'}`);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setOddClass(''), 5000);
    }
  }, [odds, prevOdds]);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    []
  );

  const onClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      if (!providerId || !marketStringId || !outcomeId) return;

      if (isSelected) {
        dispatch(removeBetFromBetSlip({ providerId, marketStringId, outcomeId }));
        return;
      }

      const matchPayload = buildOutrightMatch(sourceEvent);
      dispatch(
        addBetToBetSlip({
          providerId,
          marketStringId,
          marketId: liveMarket.id,
          outcomeId,
          outcomeName: liveOutcome.name,
          odds,
          match: matchPayload
        })
      );
    },
    [
      dispatch,
      disabled,
      isSelected,
      providerId,
      marketStringId,
      outcomeId,
      liveMarket?.id,
      liveOutcome?.name,
      odds,
      sourceEvent
    ]
  );

  const oddBetBox = betBox == undefined || betBox === '' ? 'betOddsBox' : betBox;

  if (disabled) {
    return (
      <div
        className={`justify-content-center w-100 px-1 text-center text-muted light-black-svg ${oddBetBox}`}>
        <img width="20" height="16" src={`${AWS_BUCKET_URL}/Icons/lockicon.svg`} />
      </div>
    );
  }

  const matchLike = useMemo(() => buildOutrightMatch(sourceEvent), [sourceEvent]);

  const oddsText =
    odds != null
      ? oddsTransformer.transformWithDisplayFormatted(
          false,
          true,
          liveMarket?.id,
          matchLike?.gameDate ? new Date(matchLike.gameDate) : new Date(),
          odds,
          marketStringId
        )
      : '-';

  return (
    <div
      key={outcomeId}
      className={(isSelected ? 'betOddsBox-selected' : oddBetBox) + oddClass}
      onClick={onClick}>
      {oddsText}
    </div>
  );
}

export default OutrightOddButton;
